import { PolicyDialogMode, WorkflowType } from '@communities/content-management/domain';
import type { Policy } from '@communities/content-management/domain';
import { useCreateCommunityPolicy, useUpdateCommunityPolicy } from '@communities/content-management/hooks';
import type { DomainError } from '@core/domain';
import { LabelSource } from '@core/graphql/types/schema';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useState, useCallback, useMemo, useEffect } from 'react';

import type { DialogFormData, DialogStep } from '../../types';
import { DialogStep as DialogStepEnum } from '../../types';
import { transformDialogDataToPolicyInput } from '../../utils/dataTransformers';

import type { DialogActions, DialogContextValue } from './DialogContext';
import { DialogContext } from './DialogContext';

/**
 * Helper function to create form data from an existing policy
 */
function createFormDataFromPolicy(policy: Policy, isDuplicateMode: boolean, standardLabelsCatalog: StandardLabel[]): DialogFormData {
  const customLabels = policy.labels.filter(label => label.source === LabelSource.Custom);
  const policyStandardLabels = policy.labels.filter(label => label.source !== LabelSource.Custom);

  // Build a lookup of contentType by label ID from the standard labels catalog
  const contentTypeMap = new Map(standardLabelsCatalog.map(l => [l.id, l.contentType]));

  return {
    policyInfo: {
      name: isDuplicateMode ? `Copy of ${policy.name}` : policy.name,
      description: policy.description || '',
      isEnabled: policy.isEnabled,
    },
    textLabels: {
      textLabels: policyStandardLabels
        .filter(label => contentTypeMap.get(label.labelId) === ContentType.Text)
        .map(label => ({
          labelId: label.labelId,
          ...(typeof label.severity === 'number' ? { severity: label.severity } : {}),
        })),
    },
    imageLabels: {
      imageLabels: policyStandardLabels
        .filter(label => contentTypeMap.get(label.labelId) === ContentType.Image)
        .map(label => ({
          labelId: label.labelId,
          ...(typeof label.scoreThreshold === 'number' ? { scoreThreshold: label.scoreThreshold } : {}),
        })),
    },
    usernameLabels: {
      usernameLabels: policyStandardLabels
        .filter(label => contentTypeMap.get(label.labelId) === ContentType.Username)
        .map(label => ({
          labelId: label.labelId,
          ...(typeof label.severity === 'number' ? { severity: label.severity } : {}),
        })),
    },
    customLabels: {
      customLabels: customLabels.map(label => ({ labelId: label.labelId })),
    },
    review: {
      isValid: true,
      warnings: [],
    },
  };
}

/**
 * Helper function to create empty form data
 */
function createEmptyFormData(): DialogFormData {
  return {
    policyInfo: {
      name: '',
      description: '',
      isEnabled: true,
    },
    textLabels: {
      textLabels: [],
    },
    imageLabels: {
      imageLabels: [],
    },
    usernameLabels: {
      usernameLabels: [],
    },
    customLabels: {
      customLabels: [],
    },
    review: {
      isValid: false,
      warnings: [],
    },
  };
}

/**
 * Step validation function
 */
function validateStep(step: DialogStep, formData: DialogFormData): boolean {
  switch (step) {
    case DialogStepEnum.POLICY_INFO:
      return formData.policyInfo.name.trim().length > 0;

    case DialogStepEnum.TEXT_LABELS:
      // Text labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.IMAGE_LABELS:
      // Image labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.USERNAME_LABELS:
      // Username labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.CUSTOM_LABELS:
      // Custom labels are optional - always allow proceeding
      return true;

    case DialogStepEnum.REVIEW:
      // Only require policy info to be valid - labels are optional
      return validateStep(DialogStepEnum.POLICY_INFO, formData);

    default:
      return false;
  }
}

/**
 * Get step order based on workflow type.
 * Classification: Policy Info → Text Labels → Image Labels → Username Labels → Review
 * Moderation: Policy Info → Text Labels → Image Labels → Username Labels → Custom Labels → Review
 */
export function getStepOrder(workflowType: WorkflowType): DialogStep[] {
  if (workflowType === WorkflowType.MODERATION) {
    return [
      DialogStepEnum.POLICY_INFO,
      DialogStepEnum.TEXT_LABELS,
      DialogStepEnum.IMAGE_LABELS,
      DialogStepEnum.USERNAME_LABELS,
      DialogStepEnum.CUSTOM_LABELS,
      DialogStepEnum.REVIEW,
    ];
  }

  // Classification (default)
  return [DialogStepEnum.POLICY_INFO, DialogStepEnum.TEXT_LABELS, DialogStepEnum.IMAGE_LABELS, DialogStepEnum.USERNAME_LABELS, DialogStepEnum.REVIEW];
}

/**
 * Get next step in dialog flow
 */
function getNextStep(currentStep: DialogStep, stepOrder: DialogStep[]): DialogStep {
  const currentIndex = stepOrder.indexOf(currentStep);

  if (currentIndex < stepOrder.length - 1) {
    return stepOrder[currentIndex + 1];
  }

  return currentStep; // Stay on last step
}

/**
 * Get previous step in dialog flow
 */
function getPreviousStep(currentStep: DialogStep, stepOrder: DialogStep[]): DialogStep {
  const currentIndex = stepOrder.indexOf(currentStep);

  if (currentIndex > 0) {
    return stepOrder[currentIndex - 1];
  }

  return currentStep; // Stay on first step
}

/**
 * Get the index of a step in the step order
 */
function getStepIndex(step: DialogStep, stepOrder: DialogStep[]): number {
  return stepOrder.indexOf(step);
}

/**
 * Validate step transition
 */
export function validateStepTransition(fromStep: DialogStep, toStep: DialogStep, formData: DialogFormData & { stepOrder?: DialogStep[] }): boolean {
  const order = formData.stepOrder || [
    DialogStepEnum.POLICY_INFO,
    DialogStepEnum.TEXT_LABELS,
    DialogStepEnum.IMAGE_LABELS,
    DialogStepEnum.USERNAME_LABELS,
    DialogStepEnum.REVIEW,
  ];
  const fromIndex = getStepIndex(fromStep, order);
  const toIndex = getStepIndex(toStep, order);

  // Allow moving backwards
  if (toIndex <= fromIndex) {
    return true;
  }

  // When moving forward, validate all steps from the first step up to (but not including) the target step
  // This ensures policy name is always required before accessing any step beyond the first
  for (let i = 0; i < toIndex; i++) {
    if (!validateStep(order[i], formData)) {
      return false;
    }
  }

  return true;
}

/**
 * DialogProvider props interface
 */
export interface DialogProviderProps {
  children: React.ReactNode;
  initialPolicy?: Policy;
  communityId: string;
  standardLabelsCatalog: StandardLabel[];
  mode?: PolicyDialogMode;
  workflowType: WorkflowType.CLASSIFICATION | WorkflowType.MODERATION;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * DialogProvider component that manages dialog state and actions
 */
// eslint-disable-next-line max-statements
export function DialogProvider({
  children,
  initialPolicy,
  communityId,
  standardLabelsCatalog,
  mode = PolicyDialogMode.Create,
  workflowType,
  onComplete,
  onCancel,
}: DialogProviderProps) {
  // Compute mode flags
  const isEditMode = mode === PolicyDialogMode.Edit;
  const isDuplicateMode = mode === PolicyDialogMode.Duplicate;

  // Compute step order based on workflow type
  const stepOrder = useMemo(() => getStepOrder(workflowType), [workflowType]);

  // Initialize form data — standardLabelsCatalog is guaranteed loaded by parent gate
  const initialFormData = useMemo(
    () => (initialPolicy ? createFormDataFromPolicy(initialPolicy, isDuplicateMode, standardLabelsCatalog) : createEmptyFormData()),
    [initialPolicy, isDuplicateMode, standardLabelsCatalog]
  );

  const [currentStep, setCurrentStep] = useState<DialogStep>(DialogStepEnum.POLICY_INFO);
  const [formData, setFormData] = useState<DialogFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<DomainError[]>([]);

  // Compute isValid based on current step and form data
  const isValid = useMemo(() => validateStep(currentStep, formData), [currentStep, formData]);

  // Policy mutation hooks
  const { createPolicy, errors: createErrors, resetErrors: resetCreateErrors } = useCreateCommunityPolicy(communityId);
  const { updatePolicy, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateCommunityPolicy(communityId);

  // Sync hook errors to dialog state so they can be displayed by ErrorBanner.
  // Also clears stale dialog errors when hooks reset (e.g. via resetErrors()).
  useEffect(() => {
    const hookErrors = createErrors || updateErrors;

    if (hookErrors && hookErrors.length > 0) {
      setErrors(hookErrors);
    } else {
      setErrors([]);
    }
  }, [createErrors, updateErrors]);

  // Update form data action
  const updateFormData = useCallback(
    (data: Partial<DialogFormData>) => {
      setFormData(prev => ({ ...prev, ...data }));
      // Clear both dialog and hook error state when form data changes
      setErrors([]);
      resetCreateErrors();
      resetUpdateErrors();
    },
    [resetCreateErrors, resetUpdateErrors]
  );

  // Navigate to specific step
  const goToStep = useCallback(
    (step: DialogStep) => {
      const isValidTransition = validateStepTransition(currentStep, step, { ...formData, stepOrder });

      if (isValidTransition) {
        setCurrentStep(step);
      }
    },
    [currentStep, formData, stepOrder]
  );

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    const nextStep = getNextStep(currentStep, stepOrder);

    goToStep(nextStep);
  }, [currentStep, stepOrder, goToStep]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousStep(currentStep, stepOrder);

    goToStep(previousStep);
  }, [currentStep, stepOrder, goToStep]);

  // Submit dialog data.
  // Note: The mutation hooks (createPolicy/updatePolicy) catch errors internally
  // and return false, so hook errors are synced to dialog state via the useEffect above.
  const submitDialog = useCallback(async () => {
    setIsSubmitting(true);

    const policyInput = transformDialogDataToPolicyInput(formData, workflowType);

    // Use update only in edit mode; duplicate mode should create a new policy
    const success = isEditMode && initialPolicy ? await updatePolicy(communityId, initialPolicy.id, policyInput) : await createPolicy(communityId, policyInput);

    if (success) {
      // Loading state will be reset when component unmounts via onComplete
      onComplete();
    } else {
      // Hook errors are synced via useEffect; just reset loading state
      setIsSubmitting(false);
    }
  }, [formData, workflowType, initialPolicy, communityId, createPolicy, updatePolicy, onComplete, isEditMode]);

  // Cancel dialog
  const cancelDialog = useCallback(() => {
    onCancel();
  }, [onCancel]);

  // Memoize state object
  const state = useMemo(
    () => ({
      currentStep,
      formData,
      isValid,
      isSubmitting,
      errors,
    }),
    [currentStep, formData, isValid, isSubmitting, errors]
  );

  // Memoize actions to prevent unnecessary re-renders
  const actions: DialogActions = useMemo(
    () => ({
      updateFormData,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      submitDialog,
      cancelDialog,
    }),
    [updateFormData, goToStep, goToNextStep, goToPreviousStep, submitDialog, cancelDialog]
  );

  // Memoize context value
  const contextValue: DialogContextValue = useMemo(
    () => ({
      state,
      actions,
      isEditMode,
      isDuplicateMode,
      workflowType,
      stepOrder,
    }),
    [state, actions, isEditMode, isDuplicateMode, workflowType, stepOrder]
  );

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
}
